// Deterministic attempt identity (handoff §11): the same delivery, learner and
// activity always produce the same sync_key, so a retry after a timeout or a
// refresh resolves to the one existing attempt instead of creating another.
//
// The key is a UUID v5 so it fits the UUID-shaped sync_key convention used by
// Lesson_Deliveries. SHA-1 is computed here rather than with crypto.subtle,
// which is missing on plain-http LAN addresses that classroom tablets may use.

// Fixed namespace for Thuto Time learner attempts. Never change it: every
// existing attempt key depends on it.
const ATTEMPT_NAMESPACE = "5b0c8a52-7c0e-4d2e-9a47-2f1d6f3c9e10";

const rotl = (x, n) => (x << n) | (x >>> (32 - n));

export const sha1 = (bytes) => {
  const ml = bytes.length;
  const withPadding = ((ml + 9 + 63) >> 6) << 6;
  const msg = new Uint8Array(withPadding);
  msg.set(bytes);
  msg[ml] = 0x80;
  const bitLen = ml * 8;
  const view = new DataView(msg.buffer);
  view.setUint32(withPadding - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(withPadding - 4, bitLen >>> 0);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const w = new Int32Array(80);

  for (let offset = 0; offset < withPadding; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getInt32(offset + i * 4);
    for (let i = 16; i < 80; i += 1) {
      w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let i = 0; i < 80; i += 1) {
      let f;
      let k;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (rotl(a, 5) + f + e + k + w[i]) | 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
  }

  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  [h0, h1, h2, h3, h4].forEach((h, i) => outView.setInt32(i * 4, h));
  return out;
};

const uuidToBytes = (uuid) =>
  Uint8Array.from(uuid.replace(/-/g, "").match(/../g), (hex) =>
    parseInt(hex, 16)
  );

const bytesToUuid = (bytes) => {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    ""
  );
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

export const uuidV5 = (name, namespace) => {
  const nameBytes = new TextEncoder().encode(name);
  const ns = uuidToBytes(namespace);
  const input = new Uint8Array(ns.length + nameBytes.length);
  input.set(ns);
  input.set(nameBytes, ns.length);
  const hash = sha1(input).slice(0, 16);
  hash[6] = (hash[6] & 0x0f) | 0x50;
  hash[8] = (hash[8] & 0x3f) | 0x80;
  return bytesToUuid(hash);
};

export const attemptSyncKey = ({ deliveryId, learnerId, activityId }) =>
  uuidV5(`${deliveryId}:${learnerId}:${activityId}`, ATTEMPT_NAMESPACE);
